import Result from "@Core/Result";
import { ServiceBase } from "@Core/ServiceBase";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import { IRoleAssignmentDetails } from "@Models/IRoleDetails";
import { IEntity } from "@Models/IEntity";
import { IUserMinimal, IUserPayload } from "@Models/IUserDetails";
import { IEmptyObject } from "@Components/shared/types";

export default class UserGroupService extends ServiceBase {
    /*
     * Create a single group model.
     */
    public async create(model: IUserGroupDetails): Promise<Result<IEntity>> {
        return await this.requestJson({
            url: `/api/UserGroup/Create/${model.parentGroupId}/${model.ownerId}?name=${model.name}`,
            method: "POST",
        });
    }

    /*
     * Fetch a single group model.
     */
    public async get(groupId: string): Promise<Result<IUserGroupDetails>> {
        return await this.requestJson({
            url: `/api/UserGroup/Get/${groupId}`,
            method: "GET",
        });
    }

    /*
     * Get all groups for the user provided.
     */
    public async getUser(userId: string, includeRoles = false): Promise<Result<IUserGroupDetails[]>> {
        return await this.requestJson({
            url: `/api/UserGroup/GetUser/${userId}?roles=${includeRoles}`,
            method: "GET",
        });
    }

    /*
     * Get all groups.
     */
    public async getSystem(): Promise<Result<IUserGroupDetails[]>> {
        return await this.requestJson({
            url: `/api/UserGroup/GetSystem`,
            method: "GET",
        });
    }

    /*
     * Fetch user role assignments matching the group ids provided.
     */
    public async getRoles(groupIds: string[], page = 0): Promise<Result<IRoleAssignmentDetails[]>> {
        return await this.requestJson<IRoleAssignmentDetails[]>({
            url: `/api/UserGroup/GetRoles/${groupIds.join(",")}?page=${page}`,
            method: "GET",
        });
    }

    /*
     * Update an entire group model.
     */
    public async update(model: IUserGroupDetails): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/UserGroup/Update/${model.groupId}`,
            method: "PATCH",
            data: model,
        });
    }

    /*
     * Get all groups for the user provided.
     */
    public async getMembers(groupIds: string[], page = 0, isAdmin = false): Promise<Result<IUserPayload>> {
        return await this.requestJson<IUserPayload>({
            url: `/api/UserGroup/GetMembers/${groupIds.join(",")}?page=${page}${isAdmin ? "&is-admin=true" : ""}`,
            method: "GET",
        });
    }

    /*
     * Get members for the group id provided, filtered on membership and capability within the group.
     */
    public async getFilteredMembers(groupId: string, capabilityIds: string[]): Promise<Result<IUserMinimal[]>> {
        return await this.requestJson({
            url: `/api/UserGroup/GetFilteredMembers/${groupId}?capabilityIds=${capabilityIds}`,
            method: "GET",
        });
    }

    /*
     * Add users as members to a group.
     */
    public async addMembers(groupId: string, userIds: string[]): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `/api/UserGroup/AddMembers/${groupId}/${userIds.join(",")}`,
            method: "PATCH",
        });
    }

    /*
     * Remove members from a group.
     */
    public async removeMembers(userIds: string[], groupId?: string): Promise<Result<boolean>> {
        return await this.requestJson({
            url: `/api/UserGroup/RemoveMembers/${groupId}/${userIds.join(",")}`,
            method: "PATCH",
        });
    }

    /*
     * Delete a group model.
     */
    public async delete(groupId?: string): Promise<Result<IEmptyObject>> {
        return await this.requestJson({
            url: `/api/UserGroup/Delete/${groupId}`,
            method: "DELETE",
        });
    }
}
